import { supabase } from "./supabase";

export interface LigneCompte {
  numero: string;
  libelle: string;
  montant: number;
}

export interface CompteDeResultatPrimitif {
  charges: LigneCompte[];
  produits: LigneCompte[];
  totalCharges: number;
  totalProduits: number;
  resultat: number;
}

/** Compte de résultat primitif à partir des lignes affectées (classe 6 et 7 uniquement) */
export async function fetchCompteDeResultatPrimitif(
  dossierId: string | null
): Promise<CompteDeResultatPrimitif> {
  let q = supabase
    .from("transactions")
    .select("id")
    .eq("statut", "affectee");

  if (dossierId) {
    q = q.eq("dossier_id", dossierId);
  } else {
    q = q.is("dossier_id", null);
  }

  const { data: txIds, error: errTx } = await q;
  if (errTx) throw errTx;
  const ids = (txIds || []).map((r) => r.id);
  if (ids.length === 0) {
    return { charges: [], produits: [], totalCharges: 0, totalProduits: 0, resultat: 0 };
  }

  const { data: lignes, error: errLignes } = await supabase
    .from("transaction_lignes")
    .select("compte_numero, compte_libelle, montant_ht")
    .in("transaction_id", ids);

  if (errLignes) throw errLignes;

  const chargesMap = new Map<string, { libelle: string; montant: number }>();
  const produitsMap = new Map<string, { libelle: string; montant: number }>();

  for (const l of lignes || []) {
    const num = String(l.compte_numero || "").trim();
    const classe = num.charAt(0);
    const mt = Number(l.montant_ht) || 0;
    if (mt === 0) continue;

    if (classe === "6") {
      const cur = chargesMap.get(num);
      chargesMap.set(num, {
        libelle: l.compte_libelle || num,
        montant: (cur?.montant ?? 0) + mt,
      });
    } else if (classe === "7") {
      const cur = produitsMap.get(num);
      produitsMap.set(num, {
        libelle: l.compte_libelle || num,
        montant: (cur?.montant ?? 0) + mt,
      });
    }
  }

  const charges: LigneCompte[] = Array.from(chargesMap.entries())
    .map(([numero, v]) => ({ numero, libelle: v.libelle, montant: v.montant }))
    .sort((a, b) => a.numero.localeCompare(b.numero));

  const produits: LigneCompte[] = Array.from(produitsMap.entries())
    .map(([numero, v]) => ({ numero, libelle: v.libelle, montant: v.montant }))
    .sort((a, b) => a.numero.localeCompare(b.numero));

  const totalCharges = charges.reduce((s, c) => s + c.montant, 0);
  const totalProduits = produits.reduce((s, p) => s + p.montant, 0);

  return {
    charges,
    produits,
    totalCharges,
    totalProduits,
    resultat: totalProduits - totalCharges,
  };
}
